<script lang="ts">
	import { format } from 'svelte-i18n';
	import type { Device } from 'src/types/Device';
	import type { EmeterMainMetric } from 'src/helpers/emeter_main_metric';
	import {
		buildDevicePhaseHealth,
		type DevicePhaseHealth,
	} from 'src/helpers/emeter_sidepanel_helpers';
	import { getConfiguredEm3ChannelNames } from 'src/helpers/em3_channel_names';
	import { capFirstChar, convert } from 'src/helpers/utilities';
	import EmeterPhaseBars from 'src/components/EmeterData/EmeterPhaseBars.svelte';
	import { ParameterCardV2, Row, Col, Text } from '@Shelly/shelly-design-system';
	import { useNavigateToHistoryStatisticsTab } from '../Sidepanel/useNavigateToHistoryStatisticsTab';

	export let device: Device;
	export let status: Record<string, any>;
	export let metric: EmeterMainMetric = 'power';

	const { navigateToHistoryStatisticsTab } = useNavigateToHistoryStatisticsTab();

	$: channelNames = getConfiguredEm3ChannelNames(device);
	$: phaseHealth = buildDevicePhaseHealth(status, metric, channelNames) as DevicePhaseHealth;
	$: otherMetricDescription =
		metric === 'power'
			? $format('em_current', { default: 'current' })
			: $format('em_power', { default: 'power' });
</script>

{#if phaseHealth.phases.length}
	<EmeterPhaseBars
		phases={phaseHealth.phases}
		imbalancePercent={phaseHealth.imbalancePercent}
		isBalanced={phaseHealth.isBalanced}
	/>

	{#if phaseHealth.hasNeutral && phaseHealth.nCurrent != null}
		<Text styleType="h2" class="mb-s text-capitalize">
			{capFirstChar($format('label_power_n_phase', { default: 'Neutral' }))}
		</Text>
		<ParameterCardV2
			title={convert(phaseHealth.nCurrent, 'A')}
			description={$format('current_n', { default: 'Neutral current' })}
			size="s"
			class="mb-m"
		/>
	{/if}

	<Text styleType="h2" class="mb-s">
		{$format('label_emeter_phase_details', { default: 'Phase details' })}
	</Text>
	<Row class="mb-m">
		{#each phaseHealth.phases as phase (phase.phase)}
			<Col xs="4">
				<Text styleType="controls-small" class="mb-xs text-color-muted">
					{phase.displayName}
				</Text>
				{#if phase.voltage != null}
					<ParameterCardV2
						title={convert(phase.voltage, 'V')}
						description={capFirstChar($format('em_voltage', { default: 'voltage' }))}
						size="xs"
						class="mb-s"
					/>
				{/if}
				<ParameterCardV2
					title={phase.otherMetricLabel}
					description={capFirstChar(otherMetricDescription)}
					size="xs"
					class="mb-s"
				/>
			</Col>
		{/each}
	</Row>

	<Text styleType="h2" class="mb-s">
		{$format('label_emeter_diagnostics', { default: 'Diagnostics' })}
	</Text>
	{#each phaseHealth.phases as phase (phase.phase)}
		<div class="mb-s">
			<Text styleType="controls-small" class="mb-xs text-color-muted">{phase.displayName}</Text>
			<Row>
				{#if phase.aprtPower != null}
					<Col>
						<ParameterCardV2
							title={convert(phase.aprtPower, 'VA')}
							description={capFirstChar(
								$format('em_aparent_power', { default: 'apparent power' }),
							)}
							size="xs"
							class="mb-s"
						/>
					</Col>
				{/if}
				{#if phase.pf != null}
					<Col>
						<ParameterCardV2
							title={phase.pf === 0 ? 'N/A' : String(phase.pf)}
							description={capFirstChar(
								$format('em_power_factor', { default: 'power factor' }),
							)}
							size="xs"
							class="mb-s"
						/>
					</Col>
				{/if}
				{#if phase.freq != null}
					<Col>
						<ParameterCardV2
							title={`${phase.freq} Hz`}
							description={capFirstChar($format('em_freq', { default: 'frequency' }))}
							size="xs"
							class="mb-s"
						/>
					</Col>
				{/if}
			</Row>
			{#if phase.totalActEnergy != null || phase.totalActRetEnergy != null}
				<Row>
					{#if phase.totalActEnergy != null}
						<Col>
							<ParameterCardV2
								title={convert(phase.totalActEnergy, 'Wh')}
								description={capFirstChar($format('em_overall', { default: 'Overall' }))}
								size="xs"
								class="mb-s"
							/>
						</Col>
					{/if}
					{#if phase.totalActRetEnergy != null}
						<Col>
							<ParameterCardV2
								title={convert(phase.totalActRetEnergy, 'Wh')}
								description={capFirstChar(
									$format('label_total_returned', { default: 'total returned' }),
								)}
								size="xs"
								class="mb-s"
								on:click={navigateToHistoryStatisticsTab}
								isFocusable
							/>
						</Col>
					{/if}
				</Row>
			{/if}
		</div>
	{/each}
{/if}
